import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { LdButton, LdIcon, LdTypo } from "@emdgroup-liquid/liquid/dist/react";
import { ViewBaseEditingOutcome } from "@src/components/BarChart";
import { StyledColumn, StyledRow } from "@src/styles";
import { ShowTranscriptMesage } from "../../HomePage";
import {
  BarDataType,
  OutputTableResponse,
  SortProps,
  TableDefType,
  ViewPrimerModalProps,
} from "@src/types";
import { getPotentialOfftargetList } from "@src/api/api";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { paths } from "@src/router/paths";
import { IGV } from "@src/components/Igv";
import { IgvDataType } from "@src/types";
import {
  USER_ID,
  DNA_BE_FILEPATH,
  DNA_BE_FILEPATH_NAME,
  DNA_INDEX_FILEPATH_NAME,
  IGV_HOST_URL,
  ROWS_PER_PAGE,
  CRIPSER_PATH,
  excelTopLink,
  SIGMA_IMPORT_URL,
} from "@src/config";
import {
  StyledBox,
  StyledBoxInput,
  StyledBoxLabel,
  StyledDownload,
  StyledHightLightSpan,
  StyledModalFooter,
  StyledNotice,
  StyledResultTable,
} from "./ResultView.style";
import Highlighter from "react-highlight-words";
import { StyledSpanRed } from "../PotentialOfftarget/PotentialOfftarget.style";
import {
  ARR_LABELS,
  PAM_FILTER_OPTIONS,
  PRIMER_DOWNLOAD_HEADER,
  PRIMER_DOWNLOAD_KEYS,
  REG_PAM_FILTER,
} from "@src/constants";
import {
  StyledCodons,
  StyledSpanCodon,
} from "@src/components/ResultTable/ResultTable.style";
import { ShowProgress } from "@src/components/ShowProgress/ShowProgress";
import { StyledModal, StyledPanelContainer } from "../../HomePage.style";
import { RootState, useAppDispatch } from "@src/features/store/store";
import { updateOfftarget, updateResult } from "@src/features/Slices/DataSlice";
import { useSelector } from "react-redux";
import { getSortStates, sortTableData } from "@src/utils/tableUtils";
import useIframeHeight from "@src/components/IframeHeight/Iframe";
import { FormProvider, useForm } from "react-hook-form";
import { StyledForm } from "../SequenceInputForm/SequenceInputForm.style";
import { FormInput } from "@src/components";
import { useGetPrimerDesignDownload } from "@src/query/editor/EditorQuery";
import { generateUUID, timestampForFileName } from "@src/utils/utils";
import * as XLSX from "xlsx";
import { showNotification } from "@src/hooks/useNotification";

const intivalIgvValues = {
  genome: "",
  locus: "",
  filePath: "",
  indexPath: "",
  igvHost: "",
  projectId: "",
  message: undefined,
};

const loaderPropsOfftarget = {
  headerText: "In progress",
  message: "Fetching off-target details, please wait",
  cancelable: false,
};

const sleep = (delay: number) =>
  new Promise((resolve) => setTimeout(resolve, delay));

function ResultView({
  data,
  message,
  mode,
  selectedGenome,
  igv,
  selectedEditWindow,
  inputSequence = "",
  isGenomeSpecific = true,
}: any) {
  const [loading, setLoading] = useState(false);
  const [barData, setBarData] = useState<BarDataType[]>([]);
  const [byStanderData, setBystanderData] = useState<string>("");
  const [showOutcomeModal, setOutcomeModal] = useState<boolean>(false);
  const [showPrimerDesignModal, setPrimerDesignModal] =
    useState<boolean>(false);
  const [selectedResultItem, setSelectedResultItem] = useState<any | null>(
    null
  );
  const [hoveredSequence, setHoveredSequence] = useState<string>("");
  const [hoveredGRNA, setHoveredGRNA] = useState<string>("");



  const [tableDef, setTableDef] = useState<TableDefType>({
    uniqueKey: "",
    multiple: false,
    single: false,
    columns: [],
  });
  const [offtargetResultFiltered, setOfftargetResultFiltered] = useState<
    OutputTableResponse | undefined
  >(undefined);

  const [igvData, setIgvData] = useState<IgvDataType>(intivalIgvValues);
  const [selectedPAMFilter, setSelectedPAMFilter] = useState<string>("NON NGG");
  const [isPAMNGGFilter, setPAMNGGFilter] = useState<boolean | undefined>(
    false
  );
  const [seletedTableRowKeys, _setSeletedTableRowKeys] = useState<any>([]);
  const [lastSelectedID, setLastSelectedID] = useState<any>(undefined);
  const [selectedGRNAItems, setSelectedGRNAItems] = useState<
    Array<{ "gRNA ID": string; "gRNA Sequence": string }>
  >([]);

  // PAGINATION STATES
  const [isLoading, setTableLoading] = useState(false);
  const [currentPage, setcurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(Number(ROWS_PER_PAGE[0]));
  const [currentSort, setCurrenSort] = useState<SortProps>({
    sort: "createdDate",
    sortOrder: "desc",
    pIndex: null,
  });
  const [perPageData, setPerPageData] = useState<any>(undefined);
  const [sortedData, setSortedData] = useState<any>(undefined);

  const dispatch = useAppDispatch();
  const sortedDataRef = useRef<any>(null); // hold sorted data with select unselect in place

  const handlePageSizeChange = (pageSize: number) => {
    setcurrentPage(0);
    setRowsPerPage(pageSize);
  };
  const handlePageChange = (value: number) => {
    setcurrentPage(value);
  };

  const handleSort = useCallback(
    (ev: any) => {
      const { sortOrder, sort, pIndex } = getSortStates({ ev, tableDef });
      setCurrenSort({ sortOrder, sort, pIndex });
    },
    [tableDef]
  );

  const resultData = useSelector((state: RootState) => state.data.resultData);

  // check if selected gRNAs NOT available in session but selected rows present in result data, update sorted data

  useEffect(() => {
    const savedIds = sessionStorage.getItem("seletedTableRowKeys");
    const selectedRowAvail = resultData?.results_table.some(
      (item: any) => item.selected
    );
    if (!savedIds && selectedRowAvail) {
      setSortedData(resultData);
    }
  }, [resultData]);

  /** slice data after sorting to current rows per page and current page */

  useEffect(() => {
    const updateSortedData = async () => {
      if (
        offtargetResultFiltered &&
        offtargetResultFiltered.results_table.length
      ) {
        /** Update result data with checked rows if available */
        let offtrgt = JSON.parse(JSON.stringify(offtargetResultFiltered));
        if (sortedData && sortedData.results_table?.length) {
          offtrgt.results_table.forEach((item: any, index: number) => {
            const match = sortedData?.results_table?.find(
              (item2: any) => item2.gRNA_id === item.gRNA_id
            );
            item.selected = match?.selected;
          });
        }
        // updation ends
        await sleep(200); // ADDED tp prevent stop codon IGV desection issue
        const { pageData, sortedElements } = sortTableData({
          data: offtrgt?.results_table,
          currentSort,
          currentPage,
          rowsPerPage,
        });
        const pgData = { ...offtargetResultFiltered, results_table: pageData };
        const srtData = {
          ...offtargetResultFiltered,
          results_table: sortedElements,
        };
        setTableLoading(true);
        setPerPageData([]);
        setTimeout(() => {
          setPerPageData(pgData);
          setSortedData(srtData);
          setTableLoading(false);
          sortedDataRef.current = srtData;
        }, 0);
      }
    };
    updateSortedData();
  }, [currentPage, currentSort, offtargetResultFiltered, rowsPerPage]);

  // onSelect and onSelectAll for table interations

  const onSelect = useCallback(
    (ev: any) => {
      const { uniqueKey, multiple, single } = tableDef;
      // take out DATA part of sorted data
      const srtData = sortedData?.results_table;
      if (srtData && srtData.length) {
        const selected =
          typeof ev.detail.selected === "boolean"
            ? ev.detail.selected
            : ev.detail;
        const elementNumber = single
          ? ev.target.value
          : srtData[ev.detail.rowIndex + currentPage * rowsPerPage][uniqueKey];
        const updatedElements = srtData.map((e: any) => {
          return {
            ...e,
            selected:
              e[uniqueKey] === elementNumber
                ? selected
                : multiple
                  ? e.selected
                  : undefined,
          };
        });
        const pgDataNew = updatedElements?.slice(
          currentPage * rowsPerPage,
          currentPage * rowsPerPage + rowsPerPage
        );
        if (
          tableDef.onRowSelect &&
          typeof tableDef.onRowSelect === "function"
        ) {
          tableDef.onRowSelect(
            updatedElements.filter((el: any) => el.selected && el)
          );
        }
        setPerPageData({
          ...perPageData,
          results_table: pgDataNew,
        });
        setSortedData({
          ...sortedData,
          results_table: updatedElements,
        });
        sortedDataRef.current = {
          ...sortedData,
          results_table: updatedElements,
        };
      }
    },
    [currentPage, perPageData, rowsPerPage, sortedData, tableDef]
  );

  const onSelectAll = useCallback(
    (ev: any) => {
      const { multiple } = tableDef;
      if (!multiple) {
        // if multiple option is not set, dont do anything
        ev?.stopPropagation();
      } else {
        const srtData = sortedData?.results_table;
        if (srtData && srtData.length) {
          const updatedElements = srtData.map((e: any) => {
            return {
              ...e,
              selected: ev?.detail.selected,
            };
          });

          const pgDataNew = updatedElements?.slice(
            currentPage * rowsPerPage,
            currentPage * rowsPerPage + rowsPerPage
          );
          if (
            tableDef.onRowSelect &&
            typeof tableDef.onRowSelect === "function"
          ) {
            tableDef.onRowSelect(
              updatedElements.filter((el: any) => el.selected && el)
            );
          }

          setPerPageData({
            ...perPageData,
            results_table: pgDataNew,
          });
          setSortedData({
            ...sortedData,
            results_table: updatedElements,
          });

          sortedDataRef.current = {
            ...sortedData,
            results_table: updatedElements,
          };
        }
      }
    },
    [currentPage, perPageData, rowsPerPage, sortedData, tableDef]
  );

  // check/uncheck table rows when selecting IGV SiRNA IDs

  useEffect(() => {
    const updateTableRows = ({ lstSelIds }: any) => {
      if (sortedData?.results_table && lstSelIds) {
        if (!lastSelectedID?.gRNA_id?.length) {
          onSelectAll({ detail: { selected: false } });
        } else {
          const idx = sortedData?.results_table.findIndex((item: any) =>
            lstSelIds.gRNA_id.includes(item.gRNA_id)
          );
          if (idx >= 0) {
            onSelect({
              detail: {
                rowIndex: idx - currentPage * rowsPerPage,
                selected: lstSelIds.checked,
              },
            });
          }
        }
      }
    };
    updateTableRows({ lstSelIds: lastSelectedID });
  }, [lastSelectedID]);

  const selTableRowKeysRef = useRef(seletedTableRowKeys);

  //Adding logic for height
  useIframeHeight(CRIPSER_PATH);

  const updateIGVLayout = () => {
    setTimeout(async () => {
      try {
        await (window as any).igv?.visibilityChange();
      } catch { }
    }, 100);
  };
  const setSeletedTableRowKeys = (data: any) => {
    sessionStorage.setItem("seletedTableRowKeys", JSON.stringify(data));
    selTableRowKeysRef.current = data;
    _setSeletedTableRowKeys(data);
    // update IGV track color
    updateIGVLayout();
  };

  // update store with result data
  useEffect(() => {
    if (data && data.results_table) {
      dispatch(updateResult(data));
    }
  }, [data, dispatch]);

  const dataLen = (data.results_table && data.results_table.length) ?? 0;

  const navigate = useNavigate();

  const getChromsome = (chromsome: any) => {
    return Number(chromsome.replace(/[^0-9]/g, ""));
  };

  const showPotentialOfftarget = async (row: {
    base_editor_analysis_random_id: string;
    stop_codon_analysis_random_id: string;
    gRNA_id: string;
  }) => {
    setLoading(true);
    const mode = sessionStorage.getItem("mode");
    const analysis_stage =
      mode === "codon"
        ? "stop_codon_design_analysis"
        : "dna_base_editor_analysis";
    const payload = {
      user_id: USER_ID || "user_1",
      // project_dir: "/mnt/bioinfoTools/dna_base_editor_local/projects",
      base_editor_analysis_random_id:
        mode === "codon"
          ? row.stop_codon_analysis_random_id
          : row.base_editor_analysis_random_id,
      grna_id: row.gRNA_id,
      analysis_stage,
    };
    // use sorted data to update result to preserve selected status
    const sortedResult = {
      ...sortedData,
      results_table: sortedDataRef.current.results_table,
    };
    // update RESULT DATA to hold selected property
    dispatch(updateResult(sortedResult));
    const { data }: any = await getPotentialOfftargetList(payload);
    setLoading(false);
    const summary = [row].map((item: any) => {
      const arrItems = item.off_target_analysis
        .split("#")
        .map((value: string, index: number) => ({
          [ARR_LABELS[index]]: value,
        }));
      return {
        ...item,
        ...arrItems[0],
        ...arrItems[1],
        ...arrItems[2],
        ...arrItems[3],
      };
    });
    const details = {
      ...data,
      data: {
        ...data.data,
        off_targets_results: data.data.off_targets_results.map(
          (row: any, index: number) => ({
            ...row,
            unique_id: `${row.gRNA_id}_${index}`,
            chromSort: getChromsome(row.chromsome),
          })
        ),
      },
    };
    dispatch(
      updateOfftarget({
        summary,
        details,
        genome: selectedGenome,
      })
    );
    navigate(paths.offtarget);
  };

  /** get START and END position to highlight */
  const getStartAndEnd = (
    text: string,
    edit_window: string,
    strand: string
  ) => {
    let start = 0;
    let end = 0;
    if (edit_window && edit_window.length >= 2) {
      // Calculate start and end positions based on the edit window
      start =
        strand === "+"
          ? Number(edit_window[0]) - 1
          : text.length - Number(edit_window[1]);
      end =
        strand === "+"
          ? Number(selectedEditWindow[1])
          : text.length - Number(edit_window[0]) + 1;
    }
    return { start, end };
  };
  const tableDefBE = useMemo(
    () => ({
      onRowSelect: (data: any) => updateSelectedRows(data),
      tableTitle: "Result Table",
      uniqueKey: "gRNA_id",
      multiple: true,
      columns: [
        {
          name: "gRNA ID / gRNA Sequence",
          sortable: false,
          dataKey: "gRNA_id",
          dataKey2: "crispr_target",
        },
        {
          name: "Target Coding Sequence",
          sortable: false,
          dataKey: "gRNA",
          dataKey2: "gRNA_codons",
          render: (
            text: string,
            row: {
              gRNA: string;
              gRNA_codons: string;
              grna_strand: string;
              gRNA_id: string;
            }
          ) => {
            const { start, end } = getStartAndEnd(
              text,
              selectedEditWindow,
              row.grna_strand
            );
            const highlightedPart = row.gRNA.substring(start, end);

            return (
              <>
                <Highlighter
                  searchWords={[highlightedPart]}
                  autoEscape={true}
                  textToHighlight={row.gRNA}
                  highlightStyle={{
                    color: "#379a45",
                    background: "#fcfda5",
                    fontFamily: "monospace",
                    letterSpacing: "2px",
                  }}
                  unhighlightStyle={{
                    fontFamily: "monospace",
                    letterSpacing: "3px",
                  }}
                />
                <br />
                <CodonRow codons={row?.gRNA_codons} gRNA_id={row?.gRNA_id} />
              </>
            );
          },
        },

        {
          name: "Edited Target Sequence",
          sortable: true,
          dataKey: "base_edited_gRNA",
          dataKey2: "base_edited_gRNA_codons",
          render: (
            text: string,
            row: {
              base_edited_gRNA: string;
              base_edited_gRNA_codons: string;
              grna_strand: string;
              gRNA_id: string;
              gRNA: string;
            }
          ) => {
            const { start, end } = getStartAndEnd(
              text,
              selectedEditWindow,
              row.grna_strand
            );
            return (
              <>
                <Highlighter
                  searchWords={[row.base_edited_gRNA.substring(start, end)]}
                  autoEscape={true}
                  textToHighlight={row.base_edited_gRNA}
                  highlightTag={() => (
                    <Highlight gRNA={row.gRNA.substring(start, end)}>
                      {row.base_edited_gRNA.substring(start, end)}
                    </Highlight>
                  )}
                  highlightStyle={{
                    color: "#379a45",
                    background: "#fcfda5",
                    fontFamily: "monospace",
                    letterSpacing: "2px",
                  }}
                  unhighlightStyle={{
                    fontFamily: "monospace",
                    letterSpacing: "3px",
                  }}
                />
                <br />
                <CodonRow
                  codons={row?.base_edited_gRNA_codons}
                  gRNA_id={row?.gRNA_id}
                />
              </>
            );
          },
        },
        { name: "gRNA Start", sortable: true, dataKey: "gRNA_start" },
        {
          name: "PAM / gRNA Strand",
          sortable: true,
          dataKey: "strand_info",
          render: (
            strand_info: string,
            row: {
              pam_type: string;
            }
          ) => (
            <LdTypo
              style={{ paddingLeft: "10px" }}
            >{`${row.pam_type} / ${strand_info}`}</LdTypo>
          ),
        },
        {
          name: "Base Editing Outcome",
          render: (text: string, row: any) => {
            return (
              <StyledDownload
                mode="neutral-ghost"
                onClick={() =>
                  showModal(
                    row["base_editing_outcome"],
                    row["bystander_prediction"]
                  )
                }
              >
                <LdIcon name="visibility" />
              </StyledDownload>
            );
          },
        },
        ...(
          isGenomeSpecific ? [
            {
              name: "Design Primer(s)",
              render: (text: string, row: any) => {
                return (
                  <StyledDownload
                    mode="neutral-ghost"
                    onClick={() => onPrimerDesignModalClick(row)}
                  >
                    <LdIcon>
                      <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M12 3a1 1 0 0 1 1 1v9.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 13.586V4a1 1 0 0 1 1-1Z"
                          fill="#000000"
                        />
                        <path
                          d="M6 17a1 1 0 1 0-2 0v.6C4 19.482 5.518 21 7.4 21h9.2c1.882 0 3.4-1.518 3.4-3.4V17a1 1 0 1 0-2 0v.6c0 .778-.622 1.4-1.4 1.4H7.4c-.778 0-1.4-.622-1.4-1.4V17Z"
                          fill="#000000"
                        />
                      </svg>
                    </LdIcon>
                  </StyledDownload>
                );
              },
            },
          ] : []
        ),

        ...(
          igv?.locus ? [
            {
              name: "No. of mismatches",
              key: "noofmismatches",
              children: [
                {
                  name: "0",
                  dataKey: "offtargetZero",
                  key: "offtargetZero",
                  sortable: true,
                  render: (text: string, row: any) => {
                    const value = row.offtargetZero;
                    const isMultiple = Number(value) > 1;
                    return (
                      <span
                        style={{
                          color: isMultiple ? "#c53030" : "inherit",
                          fontWeight: isMultiple ? "bold" : "normal",
                        }}
                      >
                        {value}
                      </span>
                    );
                  },
                },

                {
                  name: "1",
                  dataKey: "offtargetOne",
                  key: "offtargetOne",
                  sortable: true,
                },
                {
                  name: "2",
                  dataKey: "offtargetTwo",
                  key: "offtargetTwo",
                  sortable: true,
                },
                {
                  name: "3",
                  dataKey: "offtargetThree",
                  key: "offtargetThree",
                  sortable: true,
                },
              ],
            },
            {
              name: "Off-target Details",
              key: "details",
              render: (text: string, row: any) => {
                return (
                  <LdButton
                    mode="neutral-ghost"
                    onClick={() => {
                      showPotentialOfftarget(row);
                    }}
                  >
                    <LdIcon name="visibility" />
                  </LdButton>
                );
              },
            },
          ] : []
        )

      ],
    }),
    [isGenomeSpecific]
  );

  useEffect(() => {
    // update IGV Host in window object
    if (window) {
      (window as any).IGV_HOST_URL = IGV_HOST_URL;
    }
    // add listener for IGV
    document.addEventListener("igv-track-checkbox", updateTableOnIGV);
    return () => {
      // REMOVE IGV Host in window object
      if (window) {
        (window as any).IGV_HOST_URL = undefined;
      }
      // remove listener for IGV
      document.removeEventListener("igv-track-checkbox", updateTableOnIGV);
      sessionStorage.removeItem("seletedTableRowKeys");
    };
  }, []);

  const updateTableOnIGV = (e: any) => {
    const inf = e?.detail;
    setLastSelectedID(inf);
    const savedSelRowKeys = sessionStorage.getItem("seletedTableRowKeys");
    const selRows = savedSelRowKeys ? JSON.parse(savedSelRowKeys) : [];
    if (
      inf.checked &&
      !selRows.some((el: string) => inf.gRNA_id.includes(el))
    ) {
      inf.gRNA_id.map((it: string) => selRows.push(it));
      setSeletedTableRowKeys(selRows);
    } else if (!inf.checked) {
      setSeletedTableRowKeys(
        selRows.filter((el: string) => !inf.gRNA_id.includes(el))
      );
    }
  };

  const tableDefSC = useMemo(
    () => ({
      onRowSelect: (data: any) => updateSelectedRows(data),
      tableTitle: "Result Table",
      uniqueKey: "gRNA_id",
      multiple: true,
      columns: [
        {
          name: "gRNA ID / gRNA Sequence",
          sortable: false,
          dataKey: "gRNA_id",
          dataKey2: "crispr_target",
        },
        {
          name: "Target Coding Sequence",
          sortable: false,
          dataKey: "gRNA",
          dataKey2: "gRNA_codons",
          render: (
            text: string,
            row: {
              gRNA: string;
              gRNA_codons: string;
              grna_strand: string;
              gRNA_id: string;
            }
          ) => {
            const { start, end } = getStartAndEnd(
              text,
              selectedEditWindow,
              row.grna_strand
            );
            return (
              <>
                <Highlighter
                  searchWords={[row.gRNA.substring(start, end)]}
                  autoEscape={true}
                  textToHighlight={row.gRNA}
                  highlightStyle={{
                    color: "#379a45",
                    background: "#fcfda5",
                    fontFamily: "monospace",
                    letterSpacing: "2px",
                  }}
                  unhighlightStyle={{
                    fontFamily: "monospace",
                    letterSpacing: "3px",
                  }}
                />
                <br />
                <CodonRow codons={row.gRNA_codons} gRNA_id={row?.gRNA_id} />
              </>
            );
          },
        },
        {
          name: "Edited Target Sequence",
          sortable: true,
          dataKey: "base_edited_gRNA",
          dataKey2: "base_edited_gRNA_codons",
          render: (
            text: string,
            row: {
              base_edited_gRNA: string;
              base_edited_gRNA_codons: string;
              grna_strand: string;
              gRNA_id: string;
              gRNA: string;
            }
          ) => {
            const { start, end } = getStartAndEnd(
              text,
              selectedEditWindow,
              row.grna_strand
            );
            return (
              <>
                <Highlighter
                  searchWords={[row.base_edited_gRNA.substring(start, end)]}
                  autoEscape={true}
                  textToHighlight={row.base_edited_gRNA}
                  highlightTag={() => (
                    <Highlight gRNA={row.gRNA.substring(start, end)}>
                      {row.base_edited_gRNA.substring(start, end)}
                    </Highlight>
                  )}
                  highlightStyle={{
                    color: "#379a45",
                    background: "#fcfda5",
                    fontFamily: "monospace",
                    letterSpacing: "2px",
                  }}
                  unhighlightStyle={{
                    fontFamily: "monospace",
                    letterSpacing: "3px",
                  }}
                />
                <br />
                <CodonRow
                  codons={row.base_edited_gRNA_codons}
                  gRNA_id={row?.gRNA_id}
                />
              </>
            );
          },
        },
        {
          name: "PAM / gRNA Strand",
          sortable: true,
          dataKey: "strand_info",
          render: (
            strand_info: string,
            row: {
              pam_type: string;
            }
          ) => (
            <LdTypo
              style={{ paddingLeft: "10px" }}
            >{`${row.pam_type} / ${strand_info}`}</LdTypo>
          ),
        },
        {
          name: "Position of Stop Codon",
          sortable: true,
          dataKey: "stop_codon_position",
        },
        {
          name: "Design Primer(s)",
          render: (text: string, row: any) => {
            return (
              <StyledDownload
                mode="neutral-ghost"
                onClick={() => onPrimerDesignModalClick(row)}
              >
                <LdIcon>
                  <svg
                    width="800px"
                    height="800px"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 3a1 1 0 0 1 1 1v9.586l2.293-2.293a1 1 0 0 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L11 13.586V4a1 1 0 0 1 1-1Z"
                      fill="#000000"
                    />
                    <path
                      d="M6 17a1 1 0 1 0-2 0v.6C4 19.482 5.518 21 7.4 21h9.2c1.882 0 3.4-1.518 3.4-3.4V17a1 1 0 1 0-2 0v.6c0 .778-.622 1.4-1.4 1.4H7.4c-.778 0-1.4-.622-1.4-1.4V17Z"
                      fill="#000000"
                    />
                  </svg>
                </LdIcon>
              </StyledDownload>
            );
          },
        },
        {
          name: "No. of mismatches",
          key: "noofmismatches",
          children: [
            {
              name: "0",
              dataKey: "offtargetZero",
              key: "offtargetZero",
              sortable: true,
              render: (text: string, row: any) => {
                const value = row.offtargetZero;
                const isMultiple = Number(value) > 1;
                return (
                  <span
                    style={{
                      color: isMultiple ? "#c53030" : "inherit",
                      fontWeight: isMultiple ? "bolder" : "normal",
                    }}
                  >
                    {value}
                  </span>
                );
              },
            },

            {
              name: "1",
              dataKey: "offtargetOne",
              key: "offtargetOne",
              sortable: true,
            },
            {
              name: "2",
              dataKey: "offtargetTwo",
              key: "offtargetTwo",
              sortable: true,
            },
            {
              name: "3",
              dataKey: "offtargetThree",
              key: "offtargetThree",
              sortable: true,
            },
          ],
        },
        {
          name: "Off-target Details",
          key: "details",
          render: (text: string, row: any) => {
            return (
              <LdButton
                mode="neutral-ghost"
                onClick={() => showPotentialOfftarget(row)}
              >
                <LdIcon name="visibility" />
              </LdButton>
            );
          },
        },
      ],
    }),
    []
  );

  useEffect(() => {
    setTableDef(mode && mode === "codon" ? tableDefSC : tableDefBE);
  }, [mode, tableDefSC, tableDefBE]);

  const updateTableRef = (mode: string) => {
    if (mode && mode === "codon") {
      setTableDef(tableDefSC);
    } else {
      setTableDef(tableDefBE);
    }
  };

  const updateSelectedRows = (data: any) => {
    sessionStorage.setItem(
      "seletedTableRowKeys",
      JSON.stringify(data.map((item: any) => item.gRNA_id))
    );
    setSelectedGRNAItems(
      data.map((item: any) => ({
        "gRNA ID": item.gRNA_id,
        "gRNA Sequence": item.crispr_target,
      }))
    );
    updateIGVLayout();
  };

  useEffect(() => {
    updateTableRef(mode);
  }, [mode]);

  const getFilePath = ({
    userId = "user_1",
    projectId = "project_1",
    fileName,
  }: any) => {
    return DNA_BE_FILEPATH?.concat(`${userId}/${projectId}/${fileName}`);
  };
  useEffect(() => {
    if (igv && igv.locus) {
      const filePathURL = getFilePath({
        fileName: DNA_BE_FILEPATH_NAME,
        projectId: igv?.projectId,
      });
      const indexPathURL = getFilePath({
        fileName: DNA_INDEX_FILEPATH_NAME,
        projectId: igv?.projectId,
      });
      setIgvData({
        genome: selectedGenome,
        locus: igv?.locus,
        filePath: filePathURL,
        indexPath: indexPathURL,
        igvHost: IGV_HOST_URL,
        projectId: igv?.project_id,
        message: undefined,
      });
    } else {
      setIgvData({ ...intivalIgvValues, message: "No hit to the genome" });
    }
  }, [data, igv, selectedGenome]);

  // Show Base Outcome Modal content
  const showModal = (
    data: { data_points: any; x_axis: any },
    bystander_prediction: string
  ) => {
    if (data && data.data_points?.length) {
      const bData = data.data_points.map((score: number, index: number) => ({
        score,
        position: data.x_axis[index],
        label: `\nScore: ${score.toFixed(4)} \n\n Position: ${data.x_axis[index].replace(/\n/, " - ")}`,
      }));
      setBarData(bData);
      setBystanderData(bystander_prediction);
    }

    setOutcomeModal(true);
  };

  // close base outcome modal close
  const onModalClose = () => {
    setOutcomeModal(false);
  };

  // PRIMER DESIGN
  const [loadingPrimerDownload, setLoadingPrimerDownload] =
    useState<boolean>(false);
  const { mutateAsync: getPrimerDownload } = useGetPrimerDesignDownload(
    setLoadingPrimerDownload
  );




  // Show primer design modal

  const onPrimerDesignModalClick = (selectedItem?: any) => {
    if (selectedItem) {
      setSelectedResultItem(selectedItem);
    } else {
      const sItems = sortedData?.results_table.filter(
        (el: any) => el.selected && el
      );
      const stop_codon_analysis_random_id =
        sItems[0].stop_codon_analysis_random_id;
      const base_editor_analysis_random_id =
        sItems[0].base_editor_analysis_random_id;
      const gRNA = sItems.map((item: any) => item.gRNA).join(",");
      const gRNA_id = sItems.map((item: any) => item.gRNA_id).join(",");
      setSelectedResultItem({
        stop_codon_analysis_random_id,
        base_editor_analysis_random_id,
        gRNA,
        gRNA_id,
      });
    }
    setPrimerDesignModal(true);
  };

  const onPrimerDesignModalClose = () => {
    setLoadingPrimerDownload(false);
    setPrimerDesignModal(false);
    setSelectedResultItem(null);
  };

  // Call Download primer API
  const onDownloadPrimer = async (payload: FormValuesAmplicon) => {
    const {
      stop_codon_analysis_random_id,
      base_editor_analysis_random_id,
      gRNA,
      gRNA_id,
    } = selectedResultItem;
    const source_analysis =
      mode === "codon"
        ? "stop_codon_design_analysis"
        : isGenomeSpecific
          ? "dna_base_editor_analysis"
          : "dna_base_editing_only";

    const analysis_random_id =
      mode === "codon"
        ? stop_codon_analysis_random_id
        : base_editor_analysis_random_id;
    const params = {
      user_id: USER_ID || "user_1",
      project_id: generateUUID(),
      analysis_module: "primer_design",
      source_analysis,
      source_analysis_random_id: analysis_random_id ?? "",
      grna: gRNA,
      grna_id: gRNA_id,
      table_source: "on_target",
      chromosome: "null", // null if on_target
      start_coordinate: "null", // null if on_target
      genome: selectedGenome,
      amplicon_min: payload.amplicon_min,
      amplicon_opt: payload.amplicon_opt,
      amplicon_max: payload.amplicon_max,
    };
    setLoadingPrimerDownload(true);
    const { data } = await getPrimerDownload(params);
    const arrGRNA = selectedResultItem.gRNA.split(",");
    const arrGRNA_Id = selectedResultItem.gRNA_id.split(",");
    const primerData = data?.data.map((primer: any) => {
      const gRNAIndex = arrGRNA_Id.findIndex(
        (ele: any) => ele === primer.primer_name
      );
      if (gRNAIndex !== -1) {
        return { ...primer, targetSequence: arrGRNA[gRNAIndex] };
      }
      return { ...primer };
    });

    setPrimerDesignModal(false);
    setLoadingPrimerDownload(false);
    downloadExcel(primerData);
  };

  // download excel

  const downloadExcel = (data: any) => {
    const processData = (pData: []) =>
      pData.map((item: any) =>
        PRIMER_DOWNLOAD_KEYS.flatMap((key: string) => item[key])
      );
    const workBook = XLSX.utils.book_new();
    const worksheetData = processData(data);

    // Create a hyperlink cell for the very top row (A1)
    const hyperlinkText = excelTopLink.text;
    const hyperlinkUrl = excelTopLink.link_url;
    const topLinkRow: any[] = [
      {
        t: "s",
        v: hyperlinkText,
        l: { Target: hyperlinkUrl, Tooltip: "Click to open" },
      },
    ];

    const workSheet = XLSX.utils.aoa_to_sheet([
      topLinkRow,
      PRIMER_DOWNLOAD_HEADER,
      ...worksheetData,
    ]);
    XLSX.utils.book_append_sheet(workBook, workSheet, "sheet1");
    XLSX.writeFile(workBook, `primer_design_${timestampForFileName()}.xlsx`);
  };
  // PRIMER DOWNLOAD ENDS

  const onPAMFilterChange = (filter: string[]) => {
    // reset IGV selection as page size/sort/pagination reset the selection
    setLastSelectedID(undefined);
    if (tableDef.onRowSelect && typeof tableDef.onRowSelect === "function") {
      tableDef.onRowSelect([]);
    }
    // reset code ends
    setSelectedPAMFilter(filter[0]);
  };

  useEffect(() => {
    if (resultData && resultData.results_table.length) {
      const _isPAMNGGFilter = resultData?.results_table.some((item: any) =>
        item.pam_type.match(new RegExp(REG_PAM_FILTER))
      );
      setPAMNGGFilter(_isPAMNGGFilter);
      if (_isPAMNGGFilter) {
        setSelectedPAMFilter("NGG");
      } else {
        setSelectedPAMFilter("NON NGG");
      }
    }
  }, [resultData]);

  useEffect(() => {
    if (resultData && resultData.results_table.length) {
      const newData: any = resultData?.results_table.filter(
        (item: { pam_type: string }) =>
          selectedPAMFilter === "NGG"
            ? item.pam_type.match(new RegExp(REG_PAM_FILTER))
            : !item.pam_type.match(new RegExp(REG_PAM_FILTER))
      );
      setOfftargetResultFiltered({
        ...resultData,
        results_table: newData,
      });
    }
  }, [selectedPAMFilter, resultData]);

  return (
    <StyledPanelContainer>
      <StyledRow direction="column">
        <StyledColumn width="100">
          {dataLen && !igvData?.message ? (
            <IGV data={igvData} />
          ) : (
            //<StyledNotice mode="info">{igvData?.message}</StyledNotice>
            null
          )}
          {dataLen && tableDef ? (
            <>
              {/* Multi-match warning banner — only when igv locus is present */}
              {igv?.locus && (
                <MultiMatchBanner data={sortedData?.results_table || []} />
              )}

              {!isGenomeSpecific && inputSequence && (
                <StyledBox style={{ gridTemplateColumns: "180px 1fr" }}>
                  <StyledBoxLabel>Input Sequence</StyledBoxLabel>
                  <StyledBoxInput fullWidth>
                    <HighlightedInputSequence
                      sequence={inputSequence
                        .split("\n")
                        .filter((line: string) => !line.startsWith(">"))
                        .join("\n")
                        .toUpperCase()}
                      highlight={hoveredSequence}
                      gRNA={hoveredGRNA}
                    />
                  </StyledBoxInput>
                </StyledBox>
              )}


              <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: "8px" }}>
                <LdButton
                  disabled={selectedGRNAItems.length === 0 || undefined}
                  onClick={() => {
                    const payload = selectedGRNAItems.map((item) => ({
                      name: item["gRNA ID"],
                      sequence: item["gRNA Sequence"],
                    }));
                    const form = document.createElement("form");
                    form.method = "POST";
                    form.target = "_blank";
                    form.action = SIGMA_IMPORT_URL!;
                    const input = document.createElement("input");
                    input.type = "hidden";
                    input.name = "sequences";
                    input.value = JSON.stringify(payload);
                    form.appendChild(input);
                    const designToolInput = document.createElement("input");
                    designToolInput.type = "hidden";
                    designToolInput.name = "designTool";
                    designToolInput.value = "dna_base_editor";
                    form.appendChild(designToolInput);
                    document.body.appendChild(form);
                    form.submit();
                    document.body.removeChild(form);
                  }}
                >
                  Configure & Order
                </LdButton>
              </div>

              <StyledResultTable
                data={sortedData?.results_table || []}
                tableDef={{
                  ...tableDef,
                  tableTitle: resultData?.cbe_type
                    ? `Result Table (CBE TYPE: ${resultData?.cbe_type})`
                    : "Result Table",
                }}
                enableDownload={true}
                enableFilter={
                  mode === "codon"
                    ? {
                      options: PAM_FILTER_OPTIONS.map((opt) => ({
                        ...opt,
                        disabled: opt.value === "NGG" && !isPAMNGGFilter,
                        checked: selectedPAMFilter === opt.value,
                      })),
                      onSelect: onPAMFilterChange,
                    }
                    : undefined
                }
                selectedIDs={lastSelectedID}
                selectedMode={mode}
                selectedCBEType={resultData?.cbe_type}
                onPageChange={handlePageChange}
                onSort={handleSort}
                loading={isLoading}
                totalPage={sortedData?.results_table.length}
                defaultSort={{ columnIndex: 2, sortOrder: "desc" }}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                dataInPage={perPageData?.results_table}
                onPageSizeChange={handlePageSizeChange}
                pageSize={rowsPerPage}
                onSelect={onSelect}
                onSelectAll={onSelectAll}
                onPrimerDesignSelected={onPrimerDesignModalClick}
                isGenomeSpecific={isGenomeSpecific}
                onRowMouseEnter={(element) => {
                  if (!isGenomeSpecific) {
                    const { start, end } = getStartAndEnd(
                      element.gRNA,
                      selectedEditWindow,
                      element.grna_strand
                    );
                    const highlightedPart = element.gRNA.substring(start, end);
                    setHoveredSequence(highlightedPart);
                    setHoveredGRNA(element.gRNA);
                  }
                }}
                onRowMouseLeave={() => {
                  if (!isGenomeSpecific) {
                    setHoveredSequence("");
                    setHoveredGRNA("");
                  }
                }}
              />
            </>
          ) : null}

          {message.length ? (
            <StyledRow>
              <StyledColumn width="100">
                <ShowTranscriptMesage
                  msg={message}
                  headLine="Error"
                  mode="error"
                />
              </StyledColumn>
            </StyledRow>
          ) : null}
          <ViewBaseEditingOutcome
            barData={barData}
            showModal={showOutcomeModal}
            onModalClose={onModalClose}
            byStanderData={byStanderData}
            selectedEditWindow={selectedEditWindow}
          />
          <ViewPrimerDesignModal
            onModalClose={onPrimerDesignModalClose}
            showModal={showPrimerDesignModal}
            onDownloadPrimer={onDownloadPrimer}
            loading={loadingPrimerDownload}
          />
        </StyledColumn>
      </StyledRow>
      <ShowProgress {...loaderPropsOfftarget} loading={loading} />
    </StyledPanelContainer>
  );
}

export { ResultView };

const Highlight = ({ children, gRNA }: any) => {
  const arrEditedText: [] = children?.split("");
  const arrgRNAText: [] = gRNA?.split("");
  const mapArrText = arrEditedText?.map((txt: string, index: number) => {
    return txt !== arrgRNAText[index] ? (
      <StyledSpanRed key={`styled-span-${index}`}>{txt}</StyledSpanRed>
    ) : (
      txt
    );
  });
  return <StyledHightLightSpan>{mapArrText}</StyledHightLightSpan>;
};

const CodonRow = ({ codons, gRNA_id }: { codons: string; gRNA_id: string }) => {
  const renderCodonsTail = () => (
    <>
      {codons?.substring(codons.length - 1).match(/(\w|\*)/g) && (
        <StyledSpanCodon key={`span_condon_tail_${gRNA_id}`}>
          {codons?.substring(codons.length - 1).replace(/(\w|\*)/g, "$1")}
        </StyledSpanCodon>
      )}
      {codons?.substring(codons.length - 2).match(/(\w|\*)-/g) && (
        <StyledSpanCodon key={`span_condon_tail_${gRNA_id}`}>
          {codons?.substring(codons.length - 2).replace(/(\w|\*)-/g, "$1\xa0")}
        </StyledSpanCodon>
      )}
      {codons.match(/.+\.$/g) && (
        <div
          style={{
            display: "inline-block",
            width: (codons.match(/\.+$/g) || []).length * 10,
          }}
        ></div>
      )}
    </>
  );
  if (codons.match(/^--(\w|\*)/)) {
    return (
      <StyledCodons key={`condons_${gRNA_id}`}>
        {codons
          ?.match(/(\w|\*)?--/g)
          ?.map((codon: string, index: number) => (
            <StyledSpanCodon key={`span_condon_${gRNA_id}_${index}`}>
              {codon.replace(/(\w|\*)?--/g, "\xa0$1\xa0")}
            </StyledSpanCodon>
          ))}

        {renderCodonsTail()}
      </StyledCodons>
    );
  }
  if (codons.match(/^-(\w|\*)-/)) {
    return (
      <StyledCodons key={`condons_${gRNA_id}`}>
        <StyledSpanCodon key={`span_condon_${gRNA_id}`}>
          {codons?.substring(0, 1).replace(/-/g, "\xa0")}
        </StyledSpanCodon>
        {codons
          ?.match(/(\w|\*)?--/g)
          ?.map((codon: string, index: number) => (
            <StyledSpanCodon key={`span_condon_${gRNA_id}_${index}`}>
              {codon.replace(/(\w|\*)?--/g, "\xa0$1\xa0")}
            </StyledSpanCodon>
          ))}
        {renderCodonsTail()}
      </StyledCodons>
    );
  }
  if (codons.match(/(\w|\*)--+(\w|\*)-$/g)) {
    return (
      <StyledCodons key={`condons_${gRNA_id}`}>
        {codons.match(/^\.+/g) && (
          <span
            style={{
              display: "inline-block",
              width:
                (codons.match(/^([.])+/g) || [""])[0]?.split("")?.length * 10,
            }}
          ></span>
        )}
        {codons
          ?.match(/(\w|\*)?--/g)
          ?.map((codon: string, index: number) => (
            <StyledSpanCodon key={`span_condon_${gRNA_id}_${index}`}>
              {codon.replace(/(\w|\*)?--/g, "\xa0$1\xa0")}
            </StyledSpanCodon>
          ))}
        {renderCodonsTail()}
      </StyledCodons>
    );
  }

  if (codons.match(/(\w|\*)--+(\w|\*)$/g)) {
    return (
      <StyledCodons key={`condons_${gRNA_id}`}>
        {codons.match(/^\.+/g) && (
          <span
            style={{
              display: "inline-block",
              width:
                (codons.match(/^([.])+/g) || [""])[0]?.split("")?.length * 10,
            }}
          ></span>
        )}
        {codons
          ?.match(/(\w|\*)?--/g)
          ?.map((codon: string, index: number) => (
            <StyledSpanCodon key={`span_condon_${gRNA_id}_${index}`}>
              {codon.replace(/(\w|\*)?--/g, "\xa0$1\xa0")}
            </StyledSpanCodon>
          ))}
        {renderCodonsTail()}
      </StyledCodons>
    );
  }
  return (
    <StyledCodons key={`condons_${gRNA_id}`}>
      {codons.match(/^\.+/g) && (
        <span
          style={{
            display: "inline-block",
            width:
              (codons.match(/^([.])+/g) || [""])[0]?.split("")?.length * 10,
          }}
        ></span>
      )}
      {codons
        ?.match(/(\w|\*)?--/g)
        ?.map((codon: string, index: number) => (
          <StyledSpanCodon key={`span_condon_${gRNA_id}_${index}`}>
            {codon.replace(/(\w|\*)?--/g, "\xa0$1\xa0")}
          </StyledSpanCodon>
        ))}

      {renderCodonsTail()}
    </StyledCodons>
  );
};

const MultiMatchBanner = ({ data }: { data: any[] }) => {
  const multiMatchRows = data?.filter(
    (row: any) => Number(row.offtargetZero) > 1
  );

  if (!multiMatchRows?.length) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        backgroundColor: "#fff5f5",
        border: "1px solid #c53030",
        borderRadius: "6px",
        padding: "8px 14px",
        marginBottom: "10px",
        color: "#c53030",
        fontWeight: "bold",
        fontSize: "13px",
      }}
    >
      <LdIcon>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L1.5 21h21L12 2Z" fill="#c53030" strokeLinejoin="round" />
          <line x1="12" y1="9" x2="12" y2="15"
            stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="18" r="1.2" fill="#ffffff" />
        </svg>
      </LdIcon>
      {multiMatchRows.length} guide(s) have multiple perfect matches in the genome — We strongly advise not using these guides.
    </div>
  );
};

const HighlightedInputSequence = ({
  sequence,
  highlight,
  gRNA,
}: {
  sequence: string;
  highlight: string;
  gRNA?: string;
}) => {
  const textStyle: React.CSSProperties = {
    fontFamily: "monospace",
    fontSize: "inherit",
    letterSpacing: "3px",
    lineHeight: "30px",
    wordBreak: "break-all",
  };

  const flat = sequence.replace(/\n/g, "");

  // No hover — plain sequence
  if (!gRNA || !flat.includes(gRNA)) {
    return <span style={textStyle}>{flat}</span>;
  }

  // Split by full gRNA first (level 1: blue-gray background)
  const gRNAParts = flat.split(new RegExp(`(${gRNA})`, "g"));

  return (
    <span style={textStyle}>
      {gRNAParts.map((part, i) => {
        if (part !== gRNA) {
          return <span key={i} style={textStyle}>{part}</span>;
        }

        // Within gRNA, highlight editing window (level 2: yellow)
        if (!highlight || !part.includes(highlight)) {
          return (
            <span key={i} style={{ ...textStyle, background: "#9e9e9e", borderRadius: "3px", color: "#fff", fontSize: "1.1em", fontWeight: "bold", transition: "font-size 0.15s ease" }}>
              {part}
            </span>
          );
        }

        const windowParts = part.split(new RegExp(`(${highlight})`, "g"));
        return (
          <span key={i} style={{ ...textStyle, background: "#9e9e9e", borderRadius: "3px", color: "#fff", fontSize: "1.1em", fontWeight: "bold", transition: "font-size 0.15s ease" }}>
            {windowParts.map((w, j) =>
              w === highlight ? (
                <span key={j} style={{ ...textStyle, background: "#fcfda5", color: "#379a45" }}>
                  {w}
                </span>
              ) : (
                <span key={j}>{w}</span>
              )
            )}
          </span>
        );
      })}
    </span>
  );
};




export type FormValuesAmplicon = {
  amplicon_min: string;
  amplicon_opt: string;
  amplicon_max: string;
};

const MIN_VALUE = 50;
const MAX_VALUE = 350;

export const ViewPrimerDesignModal = ({
  onModalClose,
  showModal,
  onDownloadPrimer,
  loading,
}: ViewPrimerModalProps) => {
  const modalRef = useRef<any>(null);

  const showPopupModal = () => {
    modalRef?.current?.showModal();
  };

  const hidePoupModal = useCallback(() => {
    modalRef?.current?.close();
    onModalClose();
  }, [onModalClose]);

  const onClosing = () => hidePoupModal();

  useEffect(() => {
    if (showModal) {
      showPopupModal();
    } else {
      hidePoupModal();
    }
  }, [hidePoupModal, showModal]);

  const methods = useForm<FormValuesAmplicon>({
    defaultValues: {
      amplicon_min: "150",
      amplicon_opt: "250",
      amplicon_max: "350",
    },
  });

  const downloadPrimer = async () => {
    await methods.trigger().then((isValid) => {
      if (!isValid) return;
      const payload = methods.getValues();

      // check min/opt/max values
      const { amplicon_min, amplicon_opt, amplicon_max } = payload;
      const notifications = [
        {
          condition: Number(amplicon_min) > Number(amplicon_opt),
          message: "Minimum value should not be greater than optimum value",
        },
        {
          condition: Number(amplicon_min) > Number(amplicon_max),
          message: "Minimum value should not be greater than maximum value",
        },
        {
          condition: Number(amplicon_opt) > Number(amplicon_max),
          message: "Optimum value should not be greater than maximum value",
        },
      ];
      for (const { condition, message } of notifications) {
        if (condition) {
          return showNotification({
            type: "alert",
            content: message,
          });
        }
      }
      if (typeof onDownloadPrimer === "function") {
        onDownloadPrimer(payload);
      }
    });
  };
  return (
    <StyledModal
      ref={modalRef}
      onLdmodalclosing={onClosing}
      width="800px"
      height="400px"
    >
      <LdTypo slot="header">Design Primer(s)</LdTypo>
      <FormProvider {...methods}>
        <StyledForm>
          <StyledBox>
            <StyledBoxLabel>
              PCR Amplicon Size (bp)
              <br />
              <span>
                (min: {MIN_VALUE} bp - max: {MAX_VALUE} bp)
              </span>
            </StyledBoxLabel>
            <StyledBoxInput>
              <FormInput
                name="amplicon_min"
                placeholder="Amp Min"
                label="Minimum"
                type="number"
                rules={{
                  required: {
                    value: true,
                    message: "This is a required field.",
                  },
                  validate: {
                    decimalCheck: (value) =>
                      /^\d+$/g.test(value) || "Decimal not allowed!",
                    minValueCheck: (value) =>
                      value >= MIN_VALUE || `Min Value ${MIN_VALUE}`,
                    maxValueCheck: (value) =>
                      value <= MAX_VALUE || `Max Value ${MAX_VALUE}`,
                  },
                }}
                append={{ text: "bp" }}
              />
              <FormInput
                name="amplicon_opt"
                placeholder="Amp Min"
                label="Optimum"
                type="number"
                rules={{
                  required: {
                    value: true,
                    message: "This is a required field.",
                  },
                  validate: {
                    decimalCheck: (value) =>
                      /^\d+$/g.test(value) || "Decimal not allowed!",
                    minValueCheck: (value) =>
                      value >= MIN_VALUE || `Min Value ${MIN_VALUE}`,
                    maxValueCheck: (value) =>
                      value <= MAX_VALUE || `Max Value ${MAX_VALUE}`,
                  },
                }}
                append={{ text: "bp" }}
              />
              <FormInput
                name="amplicon_max"
                placeholder="Amp Min"
                label="Maximum"
                type="number"
                rules={{
                  required: {
                    value: true,
                    message: "This is a required field.",
                  },
                  validate: {
                    decimalCheck: (value) =>
                      /^\d+$/g.test(value) || "Decimal not allowed!",
                    minValueCheck: (value) =>
                      value >= MIN_VALUE || `Min Value ${MIN_VALUE}`,
                    maxValueCheck: (value) =>
                      value <= MAX_VALUE || `Max Value ${MAX_VALUE}`,
                  },
                }}
                append={{ text: "bp" }}
              />
            </StyledBoxInput>
          </StyledBox>
          <StyledBox>
            <StyledBoxLabel position="left">
              Melting Temperature (&deg;C)
            </StyledBoxLabel>
            <StyledBoxInput>:58 - 62</StyledBoxInput>
          </StyledBox>
          <StyledBox>
            <StyledBoxLabel position="left">GC Percentage</StyledBoxLabel>
            <StyledBoxInput>:30 - 60</StyledBoxInput>
          </StyledBox>
        </StyledForm>
      </FormProvider>
      <StyledModalFooter slot="footer" className="ld-theme-bubblegum">
        <LdButton mode="secondary" onClick={onModalClose}>
          Cancel
        </LdButton>
        <LdButton
          onClick={downloadPrimer}
          progress={loading ? "pending" : undefined}
        >
          Design Download
        </LdButton>
      </StyledModalFooter>
    </StyledModal>
  );
};

const extractPureSequence = (value: string): string => {
  const lines = value.split("\n").filter(Boolean);
  if (lines.length > 0 && lines[0].includes(">")) lines.shift();
  return lines.join("");
};
